<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content">
        <section class="intro">
          <p>
            This is a quick-start checklist for adding a virtual tour to your
            Google Business Profile. For the full in-depth guide covering
            photography tips, business-type strategies, and SEO optimisation,
            read our comprehensive
            <a href="/blog/en/google-business-virtual-tour"
              >Google Business Virtual Tour Guide</a
            >.
          </p>
          <p>
            Adding a virtual tour to your Google Business Profile is one of the
            highest-ROI local SEO actions you can take in 2026. Businesses with
            virtual tours see <strong>2x more profile engagement</strong> and
            higher conversion rates from Google Maps searches. Here's the exact
            step-by-step process.
          </p>
        </section>

        <section>
          <h2>Why Add a Virtual Tour to Google Business Profile?</h2>
          <p>
            Google's algorithm rewards complete Business Profiles with higher
            local pack rankings. A virtual tour signals that your profile is
            trustworthy, verified, and high-quality. Customers who view a
            virtual tour are <strong>3x more likely to visit</strong> compared
            to those who only see photos.
          </p>
        </section>

        <section>
          <h2>Requirements</h2>
          <p>
            You need: a verified Google Business Profile, equirectangular 360°
            images (minimum 7.5 megapixels, ideally 14MP+), and either the
            Google Street View app or a Travvir export. Images must be real
            photos of your actual business — no renderings or stock images.
          </p>
        </section>

        <section>
          <h2>
            Step-by-Step: Adding a Virtual Tour to Google Business Profile
          </h2>
          <h3>Step 1 — Create Your 360° Tour with Travvir</h3>
          <p>
            Capture your business space using the Travvir app. Ensure you
            capture the exterior, entrance, and key interior areas. Export the
            tour images as equirectangular JPGs.
          </p>
          <h3>Step 2 — Open Google Business Profile</h3>
          <p>
            Go to business.google.com and select your business. Click "Photos"
            in the left menu, then select "360 photos" from the sub-menu.
          </p>
          <h3>Step 3 — Upload Your 360° Images</h3>
          <p>
            Click "Upload 360 photos." Select your equirectangular images.
            Google will automatically detect the 360° metadata (XMP data)
            embedded by Travvir and display them correctly in the viewer.
          </p>
          <h3>Step 4 — Set Your Cover Tour</h3>
          <p>
            In your Business Profile photos section, you can designate a 360°
            image as your "Interior" cover photo. This displays prominently in
            search results and Maps listings.
          </p>
          <h3>Step 5 — Connect via Street View App (Optional)</h3>
          <p>
            For a fully linked multi-room tour (vs individual 360° photos), use
            the Google Street View app to "Connect" your uploaded images into a
            navigable tour. Alternatively, embed your Travvir tour link in a
            post on your Business Profile.
          </p>
        </section>

        <section>
          <h2>Local SEO Benefits</h2>
          <p>
            After adding your virtual tour: monitor your Business Profile
            Insights for increases in "Views on Search" and "Views on Maps."
            Businesses typically see a 20–30% increase in profile actions
            (calls, directions, website clicks) within 60 days of adding a
            virtual tour.
          </p>
        </section>

        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";
const postData = blogPostsData.find(
  (p) => p.slug === "virtual-tour-google-business-profile",
);

useBlogSeo({
  title:
    "Quick Start: Add a Virtual Tour to Google Business Profile in 5 Steps",
  description:
    "5-step checklist to add a 360° virtual tour to Google Business Profile. For the full Business View / My Business guide, see our complete Google Business virtual tour article.",
  keywords:
    "add virtual tour google business profile, google business 360 upload, google maps virtual tour steps, quick guide google business tour",
  slug: "virtual-tour-google-business-profile",
  image: postData.image,
  postData,
});
</script>
